import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  getSelectedZusatzoptionen,
  GlobalActions,
  GlobalPut,
  VwgCheckoutProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  AUSLANDS_RISIKOORT,
  Auslandsrisikoort,
  isAuslandsrisikoort,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import {
  Counter,
  PrometheusCounterIncreasePayload,
  VorschadenArt,
} from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { isExtremwetterUWProcessNecessary } from '@ps20-frontend/selector';
import { VwgActions } from '../vwgActions';
import { IAppState } from '../appState';
import { ZusatzoptionenNameVWGEnum } from '../models/zusatzoptionenVWG.model';
import { mapStateToPremiumAndExtremwetterDisabledSelectorTo } from '../../app/shared/selectors/produkt.selector';
import { selectExtremwetterschutzActiveValue } from '../../app/shared/selectors/elementar-selbstbehalt.selector';

export function* watchInDashboardUebernehmenSaga() {
  yield takeEvery(VwgActions.ABSPRUNG_INS_DASHBOARD, safeSaga(generateDashboardData, App.VWG));
  yield takeEvery(VwgActions.ABSPRUNG_INS_DASHBOARD, safeSaga(generateProduktData, App.VWG));
}

export function* generateProduktData() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  const elementarZusatzoption = state.produkt.produktlinien
    .find(linie => linie.selected)
    .zusatzoptionen.find(zusatzoption => zusatzoption.name === VorschadenArt.ELEMENTAR);
  const countersIncreases: Array<PrometheusCounterIncreasePayload> = [];
  if (elementarZusatzoption && !elementarZusatzoption.disabled) {
    countersIncreases.push({
      counter: Counter.VWG_ELEMENTAR_VORBELEGT,
      increment: 1,
    });
    if (!elementarZusatzoption.isSelected) {
      countersIncreases.push({
        counter: Counter.VWG_ELEMENTAR_ABGEWAEHLT,
        increment: 1,
      });
    }
  }

  const elementarZusatzoptionSelected = Boolean(elementarZusatzoption?.isSelected);

  const isExtremwetterUWProcessNecessaryValue = isExtremwetterUWProcessNecessary(
    mapStateToPremiumAndExtremwetterDisabledSelectorTo(state),
  );

  const showEleUpload = elementarZusatzoptionSelected && isExtremwetterUWProcessNecessaryValue;

  const vwgCheckoutProdukt = {
    appId: App.VWG,
    angebotsId: state.angebotsId,
    laufzeit: state.vertragsoptionen.vertragslaufzeit,
    uebersichtTitle: 'Wohngebäudeversicherung',
    versichertesRisiko: `${state.risikoort.strasse} ${state.risikoort.hausnummer}, ${state.risikoort.plz} ${state.risikoort.ort}`,
    geokoordinatenDetails: state.risikoort.noneValidated
      ? `Längengrad: ${state.risikoort.longtitude}, Breitengrad: ${state.risikoort.latitude}`
      : undefined,
    vorvertragsnummer: state.vertragsoptionen.vorvertragsNummern,
    vorvertragSofortschutz: state.vertragsoptionen.vorvertragSofortschutz,
    countersIncreases,
    extremwetterschutzBeratunghinweis: shouldExtremwetterschutzBeratungBeDisplayed(state),
    premiumAndExtremwetterDisabled: isExtremwetterUWProcessNecessaryValue,
    beginndatum: state.vertragsoptionen.beginnDatum,
    isImmobilieImAusland: state.risikoort.isImmobilieImAusland,
    angebotsdatum: state.vertragsoptionen.angebotsdatum,
    hasVorversicherung: state.hasVorversicherung,
    showEleUpload,
  } as VwgCheckoutProduktPayload;

  vwgCheckoutProdukt.extremwetterschutzSelbstbeteiligung =
    selectExtremwetterschutzActiveValue(state);
  vwgCheckoutProdukt.zuersZone = state.gisDaten?.zuersGefaehrdungsklasse;
  yield call(globalPut.put, new GlobalActions().produktAction(vwgCheckoutProdukt));
}

export function shouldExtremwetterschutzBeratungBeDisplayed(state: IAppState): boolean {
  const produkt = state.produkt.produktlinien[0].zusatzoptionen.find(
    zusatzoption => zusatzoption.name === ZusatzoptionenNameVWGEnum.ELEMENTAR,
  );
  return Boolean(produkt) && !produkt.disabled && !produkt.isSelected;
}

export function* generateDashboardData() {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);

  yield put(new VwgActions().vorschlagdatenChanged(state.angebotsId));

  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().inDashboardUebernehmen({
      appId: App.VWG,
      angebotsId: state.angebotsId,
      kurztext: selectedProduktlinie.kurztext,
      ausgewaehlteLinie: selectedProduktlinie.produktName,
      versichertesRisiko: getVersichertesRisikoString(state.risikoort, state.gebaeude.wohnflaeche),
      versicherterRisikoort: state.risikoort,
      vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
      selectZusatzoptionen: getSelectedZusatzoptionen(selectedProduktlinie.zusatzoptionen),
      beginn: state.vertragsoptionen.beginnDatum,
      hasVorschaeden: state.vorschaeden.vorhanden,
    }),
  );
}

export function getVersichertesRisikoString(risikoort: Auslandsrisikoort, wohnflaeche: number) {
  if (isAuslandsrisikoort(risikoort)) {
    return AUSLANDS_RISIKOORT;
  } else {
    const risikoortString = `${risikoort.strasse} ${risikoort.hausnummer}, ${risikoort.plz} ${risikoort.ort}, `;
    const wohnflaecheString = `${wohnflaeche} m²`;
    return `${risikoortString}${wohnflaecheString}`;
  }
}
